<div class="modal-content" data-testid="credits-v2-modal">
  <div class="modal-header">
    <div class="modal-header__titles">
      <h2 class="modal-header__title">{{ 'CREDITS_BALANCE_V2.TITLE' | translate }}</h2>
      @if (lastUpdated$ | async; as lastUpdated) {
        <span class="modal-header__updated" data-testid="credits-v2-updated">
          {{ 'CREDITS_BALANCE_V2.LAST_UPDATE' | translate: { time: lastUpdated } }}
        </span>
      }
    </div>
    <button
      type="button"
      class="icon-button"
      (click)="closeModal()"
      [attr.aria-label]="'CREDITS_BALANCE.ALERT_CLOSE' | translate"
      data-testid="credits-v2-close"
    >
      <app-icon name="close" matFontSet="material-symbols-outlined" [size]="20" />
    </button>
  </div>

  @if (loading$ | async) {
    <div class="modal-body">
      <div class="modal-loading" [askSpinner]="true" spinnerSize="medium"></div>
    </div>
  } @else {
    @if (view$ | async; as view) {
      @if (view.showFilters) {
        <div class="modal-filters" data-testid="credits-v2-filters">
          <div class="modal-filters__row">
            <span class="modal-filters__label">{{ 'CREDITS_BALANCE_V2.FILTER_BY' | translate }}</span>
            <div class="modal-filters__pills">
              <div class="modal-filters__group">
                @for (severity of severities; track severity.value) {
                  <button
                    type="button"
                    [class]="'filter-pill -' + severity.value"
                    [class.-selected]="view.severity === severity.value"
                    [attr.aria-pressed]="view.severity === severity.value"
                    (click)="toggleSeverity(severity.value)"
                    [attr.data-testid]="'credits-v2-filter-' + severity.value"
                  >
                    @if (severity.icon) {
                      <app-icon class="filter-pill__icon" [name]="severity.icon" matFontSet="material-symbols-outlined" [size]="20" />
                    } @else {
                      <span class="filter-pill__dot"></span>
                    }
                    <span class="filter-pill__label">{{ severity.labelKey | translate: { count: view.counts[severity.value] } }}</span>
                  </button>
                }
              </div>
              <span class="modal-filters__divider"></span>
              <button
                type="button"
                class="filter-pill -bonus"
                [class.-selected]="view.bonus"
                [attr.aria-pressed]="view.bonus"
                (click)="toggleBonus()"
                data-testid="credits-v2-filter-bonus"
              >
                <span class="filter-pill__dot"></span>
                <span class="filter-pill__label">{{ 'CREDITS_BALANCE_V2.FILTER_BONUS' | translate: { count: view.counts.bonus } }}</span>
              </button>
            </div>
          </div>
          <label class="modal-search">
            <app-icon class="modal-search__icon" name="search" matFontSet="material-symbols-outlined" [size]="16" />
            <input
              #searchInput
              type="text"
              class="modal-search__input"
              [value]="view.search"
              [placeholder]="'CREDITS_BALANCE_V2.SEARCH_PLACEHOLDER' | translate"
              (input)="setSearch(searchInput.value)"
              data-testid="credits-v2-search"
            />
            @if (view.search) {
              <button
                type="button"
                class="modal-search__clear"
                (click)="setSearch('')"
                [attr.aria-label]="'CREDITS_BALANCE.ALERT_CLOSE' | translate"
                data-testid="credits-v2-search-clear"
              >
                <app-icon name="close" matFontSet="material-symbols-outlined" [size]="16" />
              </button>
            }
          </label>
        </div>
      }

      <div class="modal-body">
        @for (balance of view.balances; track balance.companyId) {
          <div
            class="credit-card"
            [class.-frozen]="balance.frozen && balance.level === 0"
            [class.-danger]="balance.level >= 2"
            [class.-warning]="balance.level === 1"
            [class.-success]="!balance.frozen && balance.level === 0"
            data-testid="credits-v2-card"
          >
            <div class="credit-card__head">
              <span class="credit-card__name" [title]="balance.name">{{ balance.name }}</span>

              <div class="credit-card__aside">
                @if (balance.level >= 1) {
                  <span class="credit-card__badge">
                    <app-icon
                      [name]="balance.level === 1 ? 'warning' : 'dangerous'"
                      matFontSet="material-symbols-outlined"
                      [size]="18"
                    />
                    <span>
                      <strong>{{ balance.remainingLabel }}</strong> {{ 'CREDITS_BALANCE_V2.REMAINING' | translate }}
                    </span>
                    <span>
                      <strong>{{ balance.used }}%</strong> {{ 'CREDITS_BALANCE.USED' | translate }}
                    </span>
                  </span>
                } @else {
                  <span class="credit-card__summary">
                    <span class="credit-card__remaining">
                      <strong>{{ balance.remainingLabel }}</strong> {{ 'CREDITS_BALANCE_V2.REMAINING' | translate }}
                    </span>
                    <span class="credit-card__used">
                      <strong>{{ balance.used }}%</strong> {{ 'CREDITS_BALANCE.USED' | translate }}
                    </span>
                  </span>
                }

                @if (balance.bonus) {
                  <span class="credit-card__tag" data-testid="credits-v2-bonus-tag">
                    {{ 'CREDITS_BALANCE_V2.BONUS_TAG' | translate }}
                  </span>
                }
              </div>
            </div>

            <div class="credit-card__track">
              <div class="credit-card__fill" [style.width]="balance.usedWidth"></div>
            </div>

            @if (balance.bonus) {
              <p class="credit-card__bonus-text">
                {{ bonusMessageKey(balance) | translate }}
                <a
                  [href]="'CREDITS_BALANCE_V2.BONUS_HELP_URL' | translate"
                  target="_blank"
                  rel="noopener noreferrer"
                >{{ 'CREDITS_BALANCE_V2.FROZEN_LEARN_MORE' | translate }}</a>
              </p>
            } @else if (balance.level === 3) {
              <p class="credit-card__empty-text">{{ 'CREDITS_BALANCE_V2.EMPTY_TEXT' | translate }}</p>
            } @else {
              <p class="credit-card__count">
                {{ 'CREDITS_BALANCE_V2.USED_OF_TOTAL' | translate: { used: balance.usedCountLabel, total: balance.totalLabel } }}
              </p>
            }

            @if (balance.level >= 2) {
              <div class="credit-card__status -danger">
                <app-icon name="dangerous" matFontSet="material-symbols-outlined" [size]="18" />
                <span>
                  <strong>{{ 'CREDITS_BALANCE_V2.CRITICAL_LABEL' | translate }}</strong>
                  {{ statusMessageKey(balance) | translate }}
                </span>
              </div>
            } @else if (balance.level === 1) {
              <div class="credit-card__status -warning">
                <app-icon name="warning" matFontSet="material-symbols-outlined" [size]="18" />
                <span>
                  <strong>{{ 'CREDITS_BALANCE_V2.WARNING_LABEL' | translate }}</strong>
                  {{ 'CREDITS_BALANCE_V2.WARNING_MESSAGE' | translate }}
                </span>
              </div>
            }
          </div>
        } @empty {
          @if (view.showFilters) {
            <p class="modal-empty" data-testid="credits-v2-empty">{{ 'CREDITS_BALANCE_V2.FILTER_EMPTY' | translate }}</p>
          }
        }
      </div>
    }
  }

  @if (showDisclaimer) {
    <div class="modal-disclaimer">
      <div class="modal-disclaimer__box" data-testid="credits-v2-disclaimer">
        <app-icon name="info" matFontSet="material-symbols-outlined" [size]="20" />
        <p>{{ 'CREDITS_BALANCE_V2.DISCLAIMER' | translate }}</p>
        <button
          type="button"
          class="icon-button"
          (click)="dismissDisclaimer()"
          [attr.aria-label]="'CREDITS_BALANCE.ALERT_CLOSE' | translate"
          data-testid="credits-v2-disclaimer-close"
        >
          <app-icon name="close" matFontSet="material-symbols-outlined" [size]="20" />
        </button>
      </div>
    </div>
  }

  <div class="modal-footer">
    @if (canManageCredits) {
      <a
        class="footer-link"
        [href]="notificationsUrl"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="credits-v2-notify"
      >
        <app-icon name="notifications_active" matFontSet="material-symbols-outlined" [size]="20" />
        <span>{{ 'CREDITS_BALANCE_V2.NOTIFY_ME' | translate }}</span>
        <app-icon name="open_in_new" matFontSet="material-symbols-outlined" [size]="20" />
      </a>
    }
    <a
      class="footer-link -secondary"
      [href]="canManageCredits ? creditsStatementUrl : ('CREDITS_BALANCE_V2.BUY_HELP_URL' | translate)"
      target="_blank"
      rel="noopener noreferrer"
      data-testid="credits-v2-buy"
    >
      <span>{{ 'CREDITS_BALANCE.BUY' | translate }}</span>
      <app-icon name="open_in_new" matFontSet="material-symbols-outlined" [size]="20" />
    </a>
  </div>
</div>
